import React, {useMemo, useCallback} from 'react';
import {issueAttributesType, fieldIssueTypesType} from '../../constants/proptype-validation';
import PropTypes from 'prop-types';
import {viewTypes} from '../../constants/view-types';
import classNames from 'classnames';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {ISSUE_ATTRIBUTES_DATA_TYPES} from '../../constants/issue-attributes';
import IssueCustomAttributeTextInlineEdit from '../forms/IssueCustomAttributeTextInlineEdit';
import IssueCustomAttributeDropdownInlineEdit from '../forms/IssueCustomAttributeDropdownInlineEdit';
import {hasPermittedAttribute} from '../../utils/issues-helpers';
import {connect} from '../../app/connect';
import {
  getCurrentViewType,
  getIssueAttributesMappingEntitiesMap,
  getIssueAttributesDefinitionsEntitiesMap,
  getIssueTypesAdminEntitiesMap,
  getContainerId
} from '../../app/selectors';

const getAttributeInlineEditComponent = customAttribute => {
  switch (customAttribute.dataType) {
    default:
    case ISSUE_ATTRIBUTES_DATA_TYPES.TEXT:
    case ISSUE_ATTRIBUTES_DATA_TYPES.PARAGRAPH:
    case ISSUE_ATTRIBUTES_DATA_TYPES.NUMERIC:
      return IssueCustomAttributeTextInlineEdit;
    case ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST:
      return IssueCustomAttributeDropdownInlineEdit;
  }
};

const IssuePreviewDetailsTabCustomAttributesSection = ({
  issue,
  viewType,
  issueCustomAttributesMappings,
  issueCustomAttributes,
  fieldIssueTypeEntitiesMap,
  containerId,
  className
}) => {
  const customAttributesMappings = useMemo(() => {
    const currentSubType = fieldIssueTypeEntitiesMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]];
    if (currentSubType) {
      return currentSubType.mappingIds.map(attribute => issueCustomAttributesMappings[attribute]);
    }
    const allTypes = fieldIssueTypeEntitiesMap[containerId];
    if (allTypes) {
      return allTypes.mappingIds.map(attribute => issueCustomAttributesMappings[attribute]);
    }
    return [];
  }, [containerId, fieldIssueTypeEntitiesMap, issue, issueCustomAttributesMappings]);

  const renderCustomAttributeInlineEdit = useCallback(customAttributeMapping => {
    const customAttribute = issueCustomAttributes[customAttributeMapping.attributeDefinitionId];

    const issueCustomAttribute = issue[ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES].find(
      issueAttribute => issueAttribute.id === customAttribute.id
    );
    const value = issueCustomAttribute ? issueCustomAttribute.value : null;
    const IssueCustomAttributeInlineEdit = getAttributeInlineEditComponent(customAttribute);
    return (
      <IssueCustomAttributeInlineEdit
        issue={issue}
        key={customAttribute.id}
        value={value}
        isRequired={customAttributeMapping.isRequired}
        customAttribute={customAttribute}
        disabled={!hasPermittedAttribute(issue, ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES)}
      />
    );
  }, [issue, issueCustomAttributes]);

  if (customAttributesMappings.length) {
    const formClassNames = classNames('IssuePreviewDetailsTabCustomAttributesSection', className, {
      'IssuePreviewDetailsTabCustomAttributesSection--narrow': viewType === viewTypes.DOCUMENT,
      'IssuePreviewDetailsTabCustomAttributesSection--wide': viewType === viewTypes.PROJECT
    });
    return (
      <form className={formClassNames} onSubmit={e => e.preventDefault()}>
        {customAttributesMappings.map(renderCustomAttributeInlineEdit)}
      </form>
    );
  }

  return null;
};

IssuePreviewDetailsTabCustomAttributesSection.displayName = 'IssuePreviewDetailsTabCustomAttributesSection';

IssuePreviewDetailsTabCustomAttributesSection.propTypes = {
  issue: issueAttributesType.isRequired,
  viewType: PropTypes.string.isRequired,
  issueCustomAttributesMappings: PropTypes.object,
  issueCustomAttributes: PropTypes.object,
  fieldIssueTypeEntitiesMap: fieldIssueTypesType.isRequired,
  containerId: PropTypes.string.isRequired,
  className: PropTypes.string
};

const mapStateToProps = state => ({
  viewType: getCurrentViewType(state),
  issueCustomAttributesMappings: getIssueAttributesMappingEntitiesMap(state),
  issueCustomAttributes: getIssueAttributesDefinitionsEntitiesMap(state),
  fieldIssueTypeEntitiesMap: getIssueTypesAdminEntitiesMap(state),
  containerId: getContainerId(state)
});

export default connect(mapStateToProps)(IssuePreviewDetailsTabCustomAttributesSection);
